import type { ReactNode } from 'react';

import { AccountMenu, type AccountMenuItem } from '@/components/AccountMenu/AccountMenu';
import { LogoMark } from '@/components/LogoMark/LogoMark';

import styles from './PageShell.module.css';

export interface PageShellProps {
  /** Shown top-right when someone is signed in. */
  signedInAs?: string | null;
  onSignOut?: () => void;
  /** Extra entries in the account menu, above Sign out. */
  accountItems?: readonly AccountMenuItem[];
  /** The harness links, rendered inside the shell's one `nav` landmark. The
   *  links themselves are the router's business, so they arrive as a slot. */
  nav?: ReactNode;
  /** Which deployment this build talks to; drawn as a badge beside the brand. */
  environment?: string | null;
  /** Drop the header and let the page fill the window, as sign-in does. */
  bare?: boolean;
  children: ReactNode;
}

/**
 * The one page frame. It renders the app's landmarks (`header`/`main`), so no
 * feature below it should add another — a second banner is a duplicate
 * landmark and a screen reader reads it as a second page header.
 */
export function PageShell({
  signedInAs,
  onSignOut,
  accountItems,
  nav,
  environment,
  bare = false,
  children,
}: PageShellProps) {
  if (bare) {
    return <main className={styles.bare}>{children}</main>;
  }

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.brand}>
            <span className={styles.mark}>
              <LogoMark size={22} />
            </span>
            LendWell <span className={styles.brandMuted}>internal portal</span>
            {environment ? <span className={styles.env}>{environment}</span> : null}
          </span>
          {nav ? (
            <nav aria-label="Harnesses" className={styles.nav}>
              {nav}
            </nav>
          ) : null}
          {signedInAs && onSignOut ? (
            <AccountMenu email={signedInAs} items={accountItems} onSignOut={onSignOut} />
          ) : null}
        </div>
      </header>
      <main className={styles.main}>{children}</main>
    </div>
  );
}
